/**
 * Homepage mobile helpers — loaded only on the index template (≤ 767 px).
 *
 * Featured-collection + products-bundle carousels (2 cards visible + peek third, swipe + arrows)
 * ---------------------------------------------------------------------------
 * Splide JS and native scroll fight over layout. We apply inline styles with
 * `setProperty(..., 'important')` after Splide mounts, disable Splide drag, and
 * retry on lazy tab / carousel injection.
 */
(function () {
  if (!window.matchMedia('(max-width: 767px)').matches) return;

  /*
   * Card image sliders (hover-to-reveal) mount via x-intersect as each card
   * nears the viewport. Each Splide mount forces a full layout. On a homepage
   * full of cards that blocks scrolling and taps until the badges paint.
   * Mobile has no hover, so keep the first photo and skip the mount.
   */
  function isCardSlideshow(el) {
    if (!el) return false;
    if (el.id && el.id.indexOf('x-slideshow-card-product') === 0) return true;
    return !!(el.closest && el.closest('.card-product') && el.classList && el.classList.contains('splide'));
  }

  var splideQueue = [];
  var splidePumping = false;

  function pumpSplideQueue() {
    if (splidePumping) return;
    splidePumping = true;
    setTimeout(function run() {
      var job = splideQueue.shift();
      if (!job) {
        splidePumping = false;
        return;
      }
      try { job(); } catch (e) { /* keep the queue moving */ }
      setTimeout(run, 0);
    }, 0);
  }

  function wrapSplideLoad(store) {
    if (!store || store._rathnaLoadWrapped || typeof store.load !== 'function') return;
    var origLoad = store.load;
    store.load = function (el) {
      if (isCardSlideshow(el)) {
        if (el.classList) el.classList.add('rathna-card-splide-skipped');
        return;
      }
      var args = arguments;
      var ctx = this;
      splideQueue.push(function () { origLoad.apply(ctx, args); });
      pumpSplideQueue();
    };
    store._rathnaLoadWrapped = true;
  }

  function hookAlpineStore() {
    if (!window.Alpine || Alpine._rathnaStoreHook) return;
    Alpine._rathnaStoreHook = true;
    var orig = Alpine.store.bind(Alpine);
    Alpine.store = function (name, value) {
      if (arguments.length > 1 && name === 'xSplide' && value) wrapSplideLoad(value);
      return arguments.length > 1 ? orig(name, value) : orig(name);
    };
    try {
      wrapSplideLoad(orig('xSplide'));
    } catch (e) { /* store not registered yet */ }
  }

  if (window.__onAlpineInit) {
    window.__onAlpineInit(hookAlpineStore);
  } else {
    document.addEventListener('alpine:init', hookAlpineStore);
  }

  var SECTION_SELECTORS = [
    '[id*="featured_collection_budget"]',
    '[id*="featured_collection_hiHaQJ"]',
    '[id*="e32885d8"]',
    '[id*="products_bundle"]',
    '[id*="collection_list_carousel_LUFhkj"]',
    '[id*="collage_D4GGTK"]'
  ];

  var CAROUSEL_PADDING = 20; /* 1.25rem left gutter only — right edge peeks */
  var PEEK_DIVISOR = 2.22 / 1.1; /* 2 full cards + peek; card width +10% vs 2.22 baseline */

  function imp(el, prop, val) {
    el.style.setProperty(prop, val, 'important');
  }

  var CAROUSEL_PROPS = [
    'overflow-x', 'overflow-y', 'scroll-snap-type', '-webkit-overflow-scrolling',
    'scrollbar-width', 'width', 'display', 'flex-wrap', 'gap', 'transform',
    'height', 'padding', 'margin', 'max-width', 'min-width', 'flex-shrink',
    'flex', 'margin-right', 'box-sizing', 'overflow', 'scroll-snap-align',
    'scroll-snap-stop', 'touch-action'
  ];

  function resetCardImageSplides(section) {
    section.querySelectorAll('.card-product .splide__track, .card-product .splide__list, .card-product .splide__slide').forEach(function (el) {
      CAROUSEL_PROPS.forEach(function (prop) {
        el.style.removeProperty(prop);
      });
    });
  }

  function getCarouselTracks(section) {
    return Array.from(section.querySelectorAll(
      '.collection > .splide__track, .x-block-products-list > .splide__track, .collection-list-wrapper > .splide__track, [id^="collage-"].splide > .splide__track'
    )).filter(function (track) {
      return (
        !track.closest('.card-product') &&
        !track.closest('.rathna-occasion-picker') &&
        !track.closest('#occasion-root')
      );
    });
  }

  /** Occasion essentials vertical picker — strip mobile carousel inline styles (runs before picker class exists). */
  window.rathnaResetOccasionBundleListScroll = function (sectionEl) {
    if (!sectionEl || !sectionEl.querySelector) return;
    var list = sectionEl.querySelector('.x-block-products-list');
    if (!list) return;
    var track = list.querySelector(':scope > .splide__track');
    if (!track) return;
    var listEl = track.querySelector(':scope > .splide__list');
    var nodes = [track];
    if (listEl) nodes.push(listEl);
    if (listEl) {
      listEl.querySelectorAll(
        ':scope > .splide__slide, :scope > [class*="bundle-splide-slide-"]'
      ).forEach(function (slide) {
        nodes.push(slide);
      });
    }
    nodes.forEach(function (el) {
      CAROUSEL_PROPS.forEach(function (prop) {
        el.style.removeProperty(prop);
      });
    });
    track._mobileCarouselReady = false;
  };

  function disableSplideDrag(carousel) {
    if (!carousel || carousel._splideDragDisabled) return;
    var splide = carousel.splide;
    if (!splide) return;

    try {
      splide.options.drag = false;
      splide.options.autoplay = false;
      splide.options.rewind = false;
      if (splide.Components && splide.Components.Autoplay) {
        splide.Components.Autoplay.pause();
      }
      if (splide.Components && splide.Components.Drag) {
        splide.Components.Drag.disable(true);
      }
    } catch (e) { /* Splide API may vary */ }

    carousel._splideDragDisabled = true;
  }

  function lockSplideListTransform(list) {
    if (!list || list._splideTransformLocked) return;
    list._splideTransformLocked = true;
    imp(list, 'transform', 'none');
    var obs = new MutationObserver(function () {
      if (list.style.transform && list.style.transform !== 'none') {
        imp(list, 'transform', 'none');
      }
    });
    obs.observe(list, { attributes: true, attributeFilter: ['style'] });
  }

  /* Let vertical swipes scroll the page; only lock horizontal when intent is clearly sideways */
  function wireGestureAxisLock(track) {
    if (track._gestureAxisWired) return;
    track._gestureAxisWired = true;

    var startX = 0;
    var startY = 0;
    var axis = null;
    var THRESHOLD = 10;

    track.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      axis = null;
    }, { passive: true });

    track.addEventListener('touchmove', function (e) {
      if (e.touches.length !== 1) return;
      var dx = e.touches[0].clientX - startX;
      var dy = e.touches[0].clientY - startY;

      if (!axis && (Math.abs(dx) > THRESHOLD || Math.abs(dy) > THRESHOLD)) {
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }

      if (axis === 'y') {
        track.style.setProperty('overflow-x', 'hidden', 'important');
      }
    }, { passive: true });

    function resetAxis() {
      axis = null;
      imp(track, 'overflow-x', 'auto');
    }

    track.addEventListener('touchend', resetAxis, { passive: true });
    track.addEventListener('touchcancel', resetAxis, { passive: true });
  }

  function getSlideWidth(trackW) {
    var gap = 12;
    var viewportW = document.documentElement.clientWidth || window.innerWidth;
    var basis = trackW > 0 ? trackW : Math.max(0, viewportW - CAROUSEL_PADDING);
    return Math.max(120, Math.floor((basis - gap) / PEEK_DIVISOR));
  }

  function setupCarousel(section) {
    if (!section) return false;
    if (section.closest('#occasion-root')) return false;

    resetCardImageSplides(section);

    var tracks = getCarouselTracks(section);
    if (!tracks.length) return false;

    var anyReady = false;

    tracks.forEach(function (track) {
      var list = track.querySelector('.splide__list');
      if (!list) return;

      var trackW = track.clientWidth;
      if (trackW <= 0) return;

      var gap = 12;
      var slideW = section.id && section.id.indexOf('collection_list_carousel_LUFhkj') !== -1
        ? Math.max(120, Math.floor((trackW - gap) / 2.45))
        : getSlideWidth(trackW);
      var slides = Array.from(list.querySelectorAll(
        ':scope > .splide__slide, :scope > [class*="preload-slide-mobile--"], :scope > [class*="preload-slide--"], :scope > [class*="bundle-splide-slide-"], :scope > [class*="collage-splide-slide-"]'
      ));
      if (!slides.length) return;

      var carousel = track.closest('.collection, .x-block-products-list, .collection-list-wrapper, [id^="collage-"].splide');
      disableSplideDrag(carousel);

      imp(track, 'overflow-x', 'auto');
      imp(track, 'overflow-y', 'hidden');
      imp(track, 'scroll-snap-type', 'x mandatory');
      imp(track, '-webkit-overflow-scrolling', 'touch');
      imp(track, 'scrollbar-width', 'none');
      imp(track, 'touch-action', 'manipulation');
      imp(track, 'width', '100%');

      if (!track._scrollbarStyleAdded) {
        var host = track.closest('[id]');
        if (host && host.id) {
          var st = document.createElement('style');
          st.textContent =
            '#' + host.id + ' .collection > .splide__track::-webkit-scrollbar{display:none}' +
            '#' + host.id + ' .x-block-products-list > .splide__track::-webkit-scrollbar{display:none}' +
            '#' + host.id + ' .collection-list-wrapper > .splide__track::-webkit-scrollbar{display:none}' +
            '#' + host.id + ' > .splide__track::-webkit-scrollbar{display:none}';
          document.head.appendChild(st);
          track._scrollbarStyleAdded = true;
        }
      }

      imp(list, 'display', 'flex');
      imp(list, 'flex-wrap', 'nowrap');
      imp(list, 'gap', gap + 'px');
      imp(list, 'transform', 'none');
      imp(list, 'width', 'auto');
      imp(list, 'height', 'auto');
      imp(list, 'padding', '0');
      imp(list, 'margin', '0');
      lockSplideListTransform(list);

      slides.forEach(function (slide) {
        imp(slide, 'width', slideW + 'px');
        imp(slide, 'max-width', slideW + 'px');
        imp(slide, 'min-width', slideW + 'px');
        imp(slide, 'flex', '0 0 ' + slideW + 'px');
        imp(slide, 'flex-shrink', '0');
        imp(slide, 'margin-right', '0');
        imp(slide, 'box-sizing', 'border-box');
        imp(slide, 'overflow', 'hidden');
        imp(slide, 'scroll-snap-align', 'start');
        slide.style.removeProperty('scroll-snap-stop');
      });

      var scrollStep = slideW + gap;

      var arrowHost = carousel || section;
      var prevBtn = arrowHost.querySelector('.splide__arrow--prev');
      var nextBtn = arrowHost.querySelector('.splide__arrow--next');

      if (prevBtn && !prevBtn._mobileWired) {
        prevBtn._mobileWired = true;
        prevBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          track.scrollBy({ left: -scrollStep, behavior: 'smooth' });
        });
      }
      if (nextBtn && !nextBtn._mobileWired) {
        nextBtn._mobileWired = true;
        nextBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          track.scrollBy({ left: scrollStep, behavior: 'smooth' });
        });
      }

      function refreshArrows() {
        var atStart = track.scrollLeft <= 4;
        var atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
        if (prevBtn) {
          prevBtn.disabled = atStart;
          prevBtn.style.opacity = atStart ? '0.3' : '';
        }
        if (nextBtn) {
          nextBtn.disabled = atEnd;
          nextBtn.style.opacity = atEnd ? '0.3' : '';
        }
      }

      if (!track._mobileScrollWired) {
        track._mobileScrollWired = true;
        track.addEventListener('scroll', refreshArrows, { passive: true });
      }
      wireGestureAxisLock(track);
      refreshArrows();

      track._mobileCarouselReady = true;
      anyReady = true;
    });

    return anyReady;
  }

  var EXTRA_HORIZONTAL_RAIL_SELECTORS = [
    '#occasion-root .x-block-products-list:not(.rathna-occasion-picker) .splide__track',
    '#occasion-root .rathna-occasion-tabs',
    '.rathna-campaign-cards',
    '.rathna-corp-products__grid'
  ];

  function wireExtraHorizontalRails() {
    EXTRA_HORIZONTAL_RAIL_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (track) {
        imp(track, 'touch-action', 'pan-x pan-y');
        wireGestureAxisLock(track);
      });
    });
  }

  function setupAll() {
    var allOk = true;
    SECTION_SELECTORS.forEach(function (sel) {
      var section = document.querySelector(sel);
      if (!section) return;
      if (!setupCarousel(section)) allOk = false;
    });
    wireExtraHorizontalRails();
    return allOk;
  }

  function trySetup(attemptsLeft) {
    var ok = setupAll();
    if (!ok && attemptsLeft > 0) {
      var delay = attemptsLeft === 3 ? 300 : attemptsLeft === 2 ? 800 : 1600;
      setTimeout(function () { trySetup(attemptsLeft - 1); }, delay);
    }
  }

  function resetSectionCarousels(section) {
    if (!section) return;
    getCarouselTracks(section).forEach(function (track) {
      track._mobileCarouselReady = false;
    });
    section.querySelectorAll('.collection, .x-block-products-list, .collection-list-wrapper, [id^="collage-"].splide').forEach(function (carousel) {
      carousel._splideDragDisabled = false;
    });
    setupCarousel(section);
  }

  function init() {
    trySetup(3);

    SECTION_SELECTORS.forEach(function (sel) {
      var section = document.querySelector(sel);
      if (!section) return;

      var sectionId = section.id.replace('shopify-section-', '');
      document.addEventListener('eurus:' + sectionId + ':splide-ready', function () {
        setTimeout(function () { resetSectionCarousels(section); }, 0);
      });
    });

    window.addEventListener('resize', function () {
      if (!window.matchMedia('(max-width: 767px)').matches) return;
      setupAll();
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Video looping — mobile cover-flow touch scroll (layout handled by video-looping.js) */
  var VIDEO_LOOPING_SECTION = '[id*="video_looping_ijQzex"]';

  function initVideoLoopingCoverFlow() {
    var section = document.querySelector(VIDEO_LOOPING_SECTION);
    if (!section) return;

    var carousel = section.querySelector('.video-looping-mobile-cover-flow');
    if (carousel) {
      var slider = carousel.querySelector('[class*="slider-template"]');
      if (slider && !slider._coverFlowTouchWired) {
        slider._coverFlowTouchWired = true;
        imp(slider, 'touch-action', 'pan-y');
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVideoLoopingCoverFlow);
  } else {
    initVideoLoopingCoverFlow();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.matches && e.target.matches(VIDEO_LOOPING_SECTION)) {
      initVideoLoopingCoverFlow();
    }
  });
})();

(function () {
  var homepageMain = document.querySelector('main.theme-template-index');
  if (!homepageMain) return;

  var DIYA_ICON =
    homepageMain.getAttribute('data-rathna-diya-icon') ||
    (homepageMain.querySelector('.rathna-festive-section-heading__diya, .rathna-deals-week-heading__diya') || {}).src ||
    '';

  function shouldSkipFestiveSection(section) {
    if (!section || !section.classList || !section.classList.contains('shopify-section')) return true;
    if (section.querySelector('.rathna-hero')) return true;
    if (section.querySelector('.scrolling-banner')) return true;
    if (section.id && section.id.indexOf('shopify-section-template--') === 0 && section.querySelector('[id^="x-header"]')) {
      return true;
    }
    return false;
  }

  function appendFestiveRuleAndDiya(wrap) {
    if (!wrap || wrap.querySelector('.rathna-festive-section-heading__rule')) return;
    var rule = document.createElement('span');
    rule.className = 'rathna-festive-section-heading__rule rathna-deals-week-heading__rule';
    rule.setAttribute('aria-hidden', 'true');
    wrap.appendChild(rule);
    if (!DIYA_ICON) return;
    var img = document.createElement('img');
    img.className = 'rathna-festive-section-heading__diya rathna-deals-week-heading__diya';
    img.src = DIYA_ICON;
    img.alt = '';
    img.width = 44;
    img.height = 44;
    img.loading = 'lazy';
    wrap.appendChild(img);
  }

  function wrapFestiveHeading(host, heading) {
    if (!host || !heading || heading.closest('.rathna-festive-section-heading')) return false;
    if (heading.closest('.collection-title-container') || heading.closest('.rathna-diwali-offer__timer-heading')) {
      return false;
    }
    var wrap = document.createElement('div');
    wrap.className = 'rathna-festive-section-heading rathna-deals-week-heading';
    heading.parentNode.insertBefore(wrap, heading);
    wrap.appendChild(heading);
    appendFestiveRuleAndDiya(wrap);
    return true;
  }

  function findPrimaryHeading(host) {
    if (!host) return null;
    if (host.querySelector('.rathna-festive-section-heading')) return null;

    var selectors =
      ':scope > h1, :scope > h2, :scope > h3, :scope > [class^="heading-"], :scope > [class*="x-block-header"]';
    var heading = host.querySelector(selectors);
    if (heading) return { host: host, heading: heading };

    var ptWrap = host.querySelector(':scope > .pt-2.pb-2');
    if (ptWrap) {
      heading = ptWrap.querySelector('h1, h2, h3, [class^="heading-"], [class*="x-block-header"]');
      if (heading) return { host: ptWrap, heading: heading };
    }

    var mb5 = host.querySelector(':scope > .mb-5');
    if (mb5) {
      heading = mb5.querySelector('h1, h2, h3, [class^="heading-"], [class*="x-block-header"]');
      if (heading) return { host: mb5, heading: heading };
    }

    return null;
  }

  function enhanceFestiveHost(host) {
    if (!host || host.dataset.rathnaFestiveHost === '1') return;
    if (host.querySelector('.rathna-shop-category-heading')) {
      host.dataset.rathnaFestiveHost = '1';
      return;
    }
    var target = findPrimaryHeading(host);
    if (!target) return;
    if (wrapFestiveHeading(target.host, target.heading)) {
      host.dataset.rathnaFestiveHost = '1';
    }
  }

  function initFestiveSectionHeadings() {
    if (!DIYA_ICON) {
      DIYA_ICON =
        homepageMain.getAttribute('data-rathna-diya-icon') ||
        (homepageMain.querySelector('.rathna-festive-section-heading__diya, .rathna-deals-week-heading__diya') || {}).src ||
        '';
    }

    homepageMain.querySelectorAll('.shopify-section').forEach(function (section) {
      if (shouldSkipFestiveSection(section)) return;

      section.querySelectorAll('.x-block-header, .x-block-promotion-grid-header, .x-block-media-gallery-header').forEach(function (host) {
        enhanceFestiveHost(host);
      });

      section.querySelectorAll('[class*="logo-list-"] .pt-2.pb-2, .section--multicolumn .pb-5, .section--multicolumn .pb-5.md\\:pb-8').forEach(function (host) {
        enhanceFestiveHost(host);
      });
    });
  }

  /* Shop by piece — horizontal rail + chevron scroll when tiles overflow (all breakpoints) */
  var PIECE_GALLERY_SECTION = '[id*="media_gallery_piece"]';

  function impPiece(el, prop, val) {
    el.style.setProperty(prop, val, 'important');
  }

  function initPieceGallerySlider() {
    var section = document.querySelector(PIECE_GALLERY_SECTION);
    if (!section) return false;
    var wrap = section.querySelector('.rathna-piece-rail-wrap');
    var track = section.querySelector('.rathna-piece-grid') || section.querySelector('[id^="x-slide-mobile-"]');
    if (!track || !wrap) return false;

    impPiece(track, 'display', 'flex');
    impPiece(track, 'flex-wrap', 'nowrap');
    impPiece(track, 'grid-template-columns', 'none');
    impPiece(track, 'overflow-x', 'auto');
    impPiece(track, 'overflow-y', 'hidden');
    impPiece(track, '-webkit-overflow-scrolling', 'touch');
    impPiece(track, 'overscroll-behavior-x', 'contain');
    impPiece(track, 'overscroll-behavior-y', 'auto');
    /* pan-x alone blocks vertical page scroll when the finger starts on this rail */
    impPiece(track, 'touch-action', 'pan-x pan-y');
    impPiece(track, 'width', '100%');
    impPiece(track, 'max-width', '100%');
    impPiece(track, 'min-width', '0');
    impPiece(track, 'scrollbar-width', 'none');
    impPiece(wrap, 'min-width', '0');
    impPiece(wrap, 'max-width', '100%');

    /* Prefer page scroll on vertical swipes; keep horizontal rail when swipe is sideways */
    if (!track._pieceGestureAxisWired) {
      track._pieceGestureAxisWired = true;
      var startX = 0;
      var startY = 0;
      var axis = null;
      var THRESHOLD = 10;

      track.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        axis = null;
      }, { passive: true });

      track.addEventListener('touchmove', function (e) {
        if (e.touches.length !== 1) return;
        var dx = e.touches[0].clientX - startX;
        var dy = e.touches[0].clientY - startY;
        if (!axis && (Math.abs(dx) > THRESHOLD || Math.abs(dy) > THRESHOLD)) {
          axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
        if (axis === 'y') {
          track.style.setProperty('overflow-x', 'hidden', 'important');
        }
      }, { passive: true });

      function resetPieceAxis() {
        axis = null;
        impPiece(track, 'overflow-x', 'auto');
      }

      track.addEventListener('touchend', resetPieceAxis, { passive: true });
      track.addEventListener('touchcancel', resetPieceAxis, { passive: true });
    }

    track.querySelectorAll('[class*="x-block-items-media-"], [class*="cell-"]').forEach(function (cell) {
      impPiece(cell, 'flex-shrink', '0');
      impPiece(cell, 'flex-grow', '0');
    });

    var prevBtn = wrap.querySelector('.splide__arrow--prev');
    var nextBtn = wrap.querySelector('.splide__arrow--next');

    function scrollStep() {
      var cell = track.querySelector('[class*="x-block-items-media-"], [class*="cell-"]');
      if (!cell) return 120;
      var gap = parseFloat(window.getComputedStyle(track).gap || window.getComputedStyle(track).columnGap) || 12;
      return cell.offsetWidth + gap;
    }

    function syncPieceArrowY() {
      var media =
        track.querySelector('.rathna-piece-media') ||
        track.querySelector('[class*="x-block-items-media-"] > .flex.grow.relative');
      if (!media) return;
      var wrapRect = wrap.getBoundingClientRect();
      var mediaRect = media.getBoundingClientRect();
      if (!mediaRect.height) return;
      var centerY = mediaRect.top - wrapRect.top + mediaRect.height / 2;
      wrap.style.setProperty('--rathna-piece-arrow-y', centerY + 'px');
    }

    function refreshArrows() {
      var wrapWidth = wrap.clientWidth;
      if (wrapWidth > 0) {
        var nextMax = Math.floor(wrapWidth) + 'px';
        if (track.style.maxWidth !== nextMax) {
          impPiece(track, 'width', '100%');
          impPiece(track, 'max-width', nextMax);
        }
      }
      var scrollable = track.scrollWidth - track.clientWidth > 1;
      if (!scrollable) {
        var cells = track.querySelectorAll('[class*="x-block-items-media-"], [class*="cell-"]');
        var step = scrollStep();
        if (cells.length && step > 0 && wrapWidth > 0) {
          var visibleSlots = Math.max(1, Math.floor(wrapWidth / step));
          scrollable = cells.length > visibleSlots;
        }
      }
      wrap.classList.toggle('rathna-piece-rail-wrap--scrollable', scrollable);
      syncPieceArrowY();
      if (!scrollable) {
        track.scrollLeft = 0;
        if (prevBtn) prevBtn.disabled = true;
        if (nextBtn) nextBtn.disabled = true;
        return;
      }
      var atStart = track.scrollLeft <= 4;
      var atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
      if (prevBtn) prevBtn.disabled = atStart;
      if (nextBtn) nextBtn.disabled = atEnd;
    }

    if (prevBtn && !prevBtn._pieceRailWired) {
      prevBtn._pieceRailWired = true;
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        track.scrollBy({ left: -scrollStep(), behavior: 'smooth' });
      });
    }
    if (nextBtn && !nextBtn._pieceRailWired) {
      nextBtn._pieceRailWired = true;
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        track.scrollBy({ left: scrollStep(), behavior: 'smooth' });
      });
    }

    if (!track._pieceRailScrollWired) {
      track._pieceRailScrollWired = true;
      track.addEventListener('scroll', refreshArrows, { passive: true });
    }
    if (!wrap._pieceRailResizeWired) {
      wrap._pieceRailResizeWired = true;
      window.addEventListener('resize', refreshArrows, { passive: true });
    }

    refreshArrows();
    requestAnimationFrame(refreshArrows);
    if (!track._pieceRailLoadWired) {
      track._pieceRailLoadWired = true;
      window.addEventListener('load', refreshArrows, { passive: true });
    }
    if (!wrap._pieceRailResizeObserver && typeof ResizeObserver !== 'undefined') {
      wrap._pieceRailResizeObserver = new ResizeObserver(refreshArrows);
      wrap._pieceRailResizeObserver.observe(wrap);
      wrap._pieceRailResizeObserver.observe(track);
    }
    if (!track._piecePieceImagesWired) {
      track._piecePieceImagesWired = true;
      track.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) {
          img.addEventListener('load', refreshArrows, { passive: true });
        }
      });
    }
    return true;
  }

  function tryInitPieceGallerySlider(attemptsLeft) {
    var ok = initPieceGallerySlider();
    if (!ok && attemptsLeft > 0) {
      var delay = attemptsLeft === 3 ? 200 : attemptsLeft === 2 ? 600 : 1200;
      setTimeout(function () { tryInitPieceGallerySlider(attemptsLeft - 1); }, delay);
    }
  }

  function bootPieceGallerySlider() {
    tryInitPieceGallerySlider(3);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootPieceGallerySlider);
  } else {
    bootPieceGallerySlider();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && (e.target.matches && e.target.matches(PIECE_GALLERY_SECTION) || (e.target.querySelector && e.target.querySelector(PIECE_GALLERY_SECTION)))) {
      tryInitPieceGallerySlider(3);
    }
  });

  var OCCASION_TABS = [
    { id: 'products_bundle_6P93z8', label: 'Wedding Seer Varisai' },
    { id: 'products_bundle_griha', label: 'Grihapravesam' },
    { id: 'products_bundle_pongal', label: 'Pongal & Puja Special' },
    { id: 'products_bundle_gift', label: 'Return Gifting Bulk Sets' }
  ];

  function findSection(partId) {
    return homepageMain.querySelector('.shopify-section[id*="' + partId + '"]');
  }

  function findOccasionBundleSections() {
    var byId = OCCASION_TABS.map(function (t) {
      return findSection(t.id);
    }).filter(Boolean);
    if (byId.length >= 2) return byId;

    var marked = Array.from(homepageMain.querySelectorAll('[data-rathna-occasion-bundle]'));
    var fromData = marked
      .map(function (el) {
        return el.closest('.shopify-section');
      })
      .filter(Boolean);
    if (fromData.length >= 2) return fromData;

    return byId;
  }

  function dedupeOccasionPromoImages(promo) {
    if (!promo) return;
    var themeImg = promo.querySelector(
      '.animate_transition_card__image img, .promotion-block img, picture img, .x-block-promotion img'
    );
    promo.querySelectorAll('.rathna-occasion-photo__img').forEach(function (extra) {
      if (themeImg && extra !== themeImg) extra.remove();
    });
    if (themeImg) {
      themeImg.classList.add('rathna-occasion-photo__img');
    }
  }

  function restoreOccasionProductList(sectionEl) {
    var collection = sectionEl.querySelector('[id^="x-collection-"]');
    var list = sectionEl.querySelector('.x-block-products-list');
    var summary = sectionEl.querySelector('.x-block-bundle-summary');
    if (!collection || !list || !summary) return;

    if (list.parentNode !== collection) {
      collection.insertBefore(list, summary);
    }

    var sticky = sectionEl.querySelector('[id^="bundler-sticky-"]');
    var wrap = sticky && sticky.querySelector('.rathna-occasion-check');
    if (wrap && wrap.childElementCount === 0) {
      wrap.remove();
    }
  }

  function layoutOccasionPickerPriceRows(sectionEl) {
    if (!sectionEl) return;
    sectionEl.querySelectorAll('.rathna-occasion-picker .card-info').forEach(function (info) {
      if (info.querySelector(':scope > .rathna-occasion-price-row')) return;

      var price =
        info.querySelector(':scope > .x-card-price') ||
        info.querySelector(':scope > .price.x-card-price') ||
        info.querySelector(':scope > .price.flex');
      var meta = info.querySelector(':scope > .card-price-meta');
      if (!price) return;

      var wrap = document.createElement('div');
      wrap.className = 'rathna-occasion-price-row';
      info.insertBefore(wrap, price);
      wrap.appendChild(price);
      if (meta && meta.parentNode === info) {
        wrap.appendChild(meta);
      }
    });
  }

  function markOccasionProductPicker(sectionEl) {
    var list = sectionEl && sectionEl.querySelector('.x-block-products-list');
    if (list) {
      list.classList.add('rathna-occasion-picker');
    }
    layoutOccasionPickerPriceRows(sectionEl);
    if (typeof window.rathnaResetOccasionBundleListScroll === 'function') {
      window.rathnaResetOccasionBundleListScroll(sectionEl);
    }
  }

  function ensureOccasionPhotoColumn(sectionEl) {
    if (!sectionEl) return;
    restoreOccasionProductList(sectionEl);

    var collection = sectionEl.querySelector('[id^="x-collection-"]');
    if (!collection) {
      markOccasionProductPicker(sectionEl);
      return;
    }

    if (sectionEl.dataset.rathnaOccasionPhoto === '1') {
      markOccasionProductPicker(sectionEl);
      return;
    }

    var promo = collection.querySelector(':scope > .x-block-bundle-promotion');
    if (!promo) {
      promo = sectionEl.querySelector('.x-block-products-list .x-block-bundle-promotion');
      if (promo) {
        collection.insertBefore(promo, collection.firstChild);
      }
    }

    var promoImg =
      promo &&
      promo.querySelector(
        '.animate_transition_card__image img, .promotion-block img, picture img, .x-block-promotion img'
      );
    if (!promo) {
      promo = document.createElement('div');
      promo.className = 'x-block-bundle-promotion rathna-occasion-photo rathna-occasion-photo-fallback w-full';
      var firstImg = sectionEl.querySelector('.x-block-products-list .card-product img');
      if (firstImg && (firstImg.currentSrc || firstImg.src)) {
        var img = document.createElement('img');
        img.src = firstImg.currentSrc || firstImg.src;
        img.alt = '';
        img.loading = 'lazy';
        img.className = 'rathna-occasion-photo__img';
        promo.appendChild(img);
      }
      collection.insertBefore(promo, collection.firstChild);
    } else {
      promo.classList.add('rathna-occasion-photo');
      if (!promoImg) {
        var fallbackImg = sectionEl.querySelector('.x-block-products-list .card-product img');
        if (fallbackImg && (fallbackImg.currentSrc || fallbackImg.src)) {
          var imgEl = document.createElement('img');
          imgEl.src = fallbackImg.currentSrc || fallbackImg.src;
          imgEl.alt = '';
          imgEl.loading = 'lazy';
          imgEl.className = 'rathna-occasion-photo__img';
          promo.appendChild(imgEl);
        }
      }
    }

    dedupeOccasionPromoImages(promo);

    sectionEl.querySelectorAll('.x-block-bundle-promotion').forEach(function (el) {
      if (el !== promo) {
        el.setAttribute('hidden', 'hidden');
        el.classList.add('rathna-occasion-promo-hidden');
      }
    });

    if (!promo.querySelector('.rathna-occasion-photo__badge')) {
      var badge = document.createElement('span');
      badge.className = 'rathna-occasion-photo__badge badge';
      badge.textContent = 'Heirloom Grade';
      promo.appendChild(badge);
    }

    if (!promo.querySelector('.rathna-occasion-photo__caption')) {
      var caption = document.createElement('div');
      caption.className = 'rathna-occasion-photo__caption occasion-caption';
      caption.textContent = 'Made for the memories you keep';
      promo.appendChild(caption);
    }

    sectionEl.dataset.rathnaOccasionPhoto = '1';
    markOccasionProductPicker(sectionEl);
  }

  function initOccasionTabs() {
    var sections = findOccasionBundleSections();
    if (sections.length < 2) return;

    var root = document.getElementById('occasion-root');
    var body;
    var nav;

    if (!root) {
      root = document.createElement('div');
      root.id = 'occasion-root';
      root.className = 'rathna-occasion-section';

      var header = document.createElement('div');
      header.className = 'rathna-occasion-section-h section-h';
      header.innerHTML =
        '<h2>Build Your Bundle</h2>';
      root.appendChild(header);

      var wrap = document.createElement('div');
      wrap.className = 'rathna-occasion-wrap occasion-wrap';

      nav = document.createElement('div');
      nav.id = 'rathna-occasion-tabs';
      nav.className = 'rathna-occasion-tabs occasion-tabs tabs';
      nav.setAttribute('role', 'tablist');
      nav.setAttribute('aria-label', 'Occasion bundle types');
      wrap.appendChild(nav);

      body = document.createElement('div');
      body.className = 'rathna-occasion-body occasion-body';
      wrap.appendChild(body);

      root.appendChild(wrap);
      sections[0].parentNode.insertBefore(root, sections[0]);
    } else {
      body = root.querySelector('.rathna-occasion-body');
      nav = root.querySelector('#rathna-occasion-tabs');
    }

    if (!body || !nav) return;

    if (sections[0].contains(root)) {
      sections[0].parentNode.insertBefore(root, sections[0]);
    } else if (root.nextElementSibling !== sections[0]) {
      sections[0].parentNode.insertBefore(root, sections[0]);
    }

    sections.forEach(function (sec) {
      sec.classList.add('rathna-occasion-bundle-section');
      if (sec.parentNode !== body) {
        body.appendChild(sec);
      }
      restoreOccasionProductList(sec);
      ensureOccasionPhotoColumn(sec);
    });

    if (!nav.querySelector('.rathna-occasion-tabs__btn')) {
      nav.textContent = '';
      OCCASION_TABS.forEach(function (tab, index) {
        if (!sections[index]) return;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'rathna-occasion-tabs__btn';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('data-occasion-tab', tab.id);
        btn.textContent = tab.label;
        btn.addEventListener('click', function () {
          showOccasionTab(index, sections, nav);
        });
        nav.appendChild(btn);
      });
    } else {
      nav.querySelectorAll('.rathna-occasion-tabs__btn').forEach(function (btn, index) {
        if (!sections[index]) return;
        btn.onclick = function () {
          showOccasionTab(index, sections, nav);
        };
      });
    }

    function showOccasionTab(active, secs, bar) {
      secs.forEach(function (sec, i) {
        var show = i === active;
        sec.hidden = !show;
        sec.style.display = show ? '' : 'none';
        sec.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (show) {
          markOccasionProductPicker(sec);
          ensureOccasionPhotoColumn(sec);
          refreshOccasionBundleMinState(sec);
        }
      });
      bar.querySelectorAll('.rathna-occasion-tabs__btn').forEach(function (b, i) {
        b.classList.toggle('is-on', i === active);
        b.classList.toggle('on', i === active);
        b.setAttribute('aria-selected', i === active ? 'true' : 'false');
      });
    }

    showOccasionTab(1, sections, nav);
  }

  function initBuyUnderJump() {
    var gridSec = document.querySelector('[id*="promotion_grid_budget"]');
    if (!gridSec) return;

    var tiles = gridSec.querySelectorAll('.x-block-promotion');
    if (!tiles.length) return;

    tiles.forEach(function (tile, index) {
      if (index > 3) return;
      tile.addEventListener('click', function (e) {
        if (e.target.closest('.x-atb-button, .button-atc, button[type="submit"]')) return;
        if (e.target.closest('a[href]')) return;

        var link = tile.querySelector('a[href*="collections"]');
        if (!link || !link.href) return;

        e.preventDefault();
        e.stopPropagation();
        window.location.href = link.href;
      });
    });
  }

  function initBrandLogoCards() {
    var cards = document.querySelectorAll(
      '.theme-template-index [id*="logo_list_9Mkfx9"] .rathna-brand-card'
    );
    if (!cards.length) return;

    cards.forEach(function (card) {
      var img = card.querySelector('.rathna-brand-card__logo-img');
      if (!img || img.dataset.brandErrorBound === '1') return;
      img.dataset.brandErrorBound = '1';

      img.addEventListener('error', function () {
        var logo = card.querySelector('.rathna-brand-card__logo');
        var nameEl = card.querySelector('.rathna-brand-card__name');
        var fallback = img.getAttribute('data-brand-fallback') || (nameEl && nameEl.textContent) || 'Brand';
        img.remove();
        if (logo && !logo.querySelector('.rathna-brand-card__logo-text')) {
          var span = document.createElement('span');
          span.className = 'rathna-brand-card__logo-text';
          span.textContent = fallback;
          logo.appendChild(span);
        }
        card.classList.add('rathna-brand-card--static');
        card.setAttribute('data-brand-clickable', 'false');
        var hit = card.querySelector('a.rathna-brand-card__link');
        if (hit) {
          var staticWrap = document.createElement('div');
          staticWrap.className = 'rathna-brand-card__link rathna-brand-card__link--static';
          staticWrap.setAttribute('aria-disabled', 'true');
          staticWrap.innerHTML = hit.innerHTML;
          hit.replaceWith(staticWrap);
        }
        var chevron = card.querySelector('.rathna-brand-card__chevron');
        if (chevron) chevron.remove();
      });
    });
  }

  function initShopByUseCards() {
    var cards = document.querySelectorAll('.theme-template-index .rathna-use-card');
    if (!cards.length) return;

    cards.forEach(function (card) {
      var link = card.querySelector('.rathna-use-media a[href]');
      if (!link || !link.href) return;
      card.addEventListener('click', function (e) {
        if (e.target.closest('a[href]')) return;
        link.click();
      });
    });
  }

  function refreshOccasionBundleMinState(sectionEl) {
    var scope =
      sectionEl && sectionEl.classList && sectionEl.classList.contains('rathna-occasion-bundle-section')
        ? sectionEl
        : sectionEl && sectionEl.closest
          ? sectionEl.closest('.rathna-occasion-bundle-section')
          : null;
    if (!scope) return;

    var sticky = scope.querySelector('[id^="bundler-sticky-"]');
    var bundleRoot = scope.querySelector('.x-container-products-bundle');
    if (!sticky || !bundleRoot) return;

    var sectionId = sticky.id.replace('bundler-sticky-', '');
    var xData = bundleRoot.getAttribute('x-data') || '';
    var minMatch = xData.match(/xProductBundle\(\s*'[^']+'\s*,\s*(\d+)/);
    var minimumItems = minMatch ? parseInt(minMatch[1], 10) : 3;
    var productsBundle = [];

    try {
      var saved = sessionStorage.getItem('bundle-' + sectionId);
      if (saved) productsBundle = JSON.parse(saved);
    } catch (err) {
      productsBundle = [];
    }

    var distinctCount = new Set(productsBundle.map(function (item) {
      return String(item.product_id != null ? item.product_id : item.id);
    })).size;
    var met = distinctCount >= minimumItems;
    var hasItems = distinctCount > 0;
    sticky.classList.toggle('rathna-occasion-min-met', met);
    sticky.classList.toggle('rathna-occasion-has-items', hasItems);
    sticky.setAttribute('data-rathna-min-met', met ? 'true' : 'false');
    sticky.setAttribute('data-rathna-has-items', hasItems ? 'true' : 'false');
    setOccasionMinNote(sticky, distinctCount, minimumItems);
  }

  function setOccasionMinNote(sticky, qty, minimumItems) {
    var footer = sticky.querySelector('.bundler-footer');
    if (!footer) return;
    var note = footer.querySelector('.rathna-occasion-min-note');
    if (!note) {
      note = document.createElement('p');
      note.className = 'rathna-occasion-min-note';
      var button = footer.querySelector('.button-atc');
      if (button) footer.insertBefore(note, button);
      else footer.appendChild(note);
    }
    var remaining = minimumItems - qty;
    if (remaining <= 0) {
      note.hidden = true;
      note.textContent = '';
      return;
    }
    note.hidden = false;
    if (qty <= 0) {
      note.textContent = 'Add a minimum of ' + minimumItems + ' products';
    } else if (remaining === 1) {
      note.textContent = 'Add 1 more product · minimum ' + minimumItems;
    } else {
      note.textContent = 'Add ' + remaining + ' more products · minimum ' + minimumItems;
    }
  }

  function wireOccasionBundleMinimumCta() {
    var root = document.getElementById('occasion-root');
    if (!root) return;

    root.querySelectorAll('.rathna-occasion-bundle-section').forEach(function (sectionEl) {
      var bundleRoot = sectionEl.querySelector('.x-container-products-bundle');
      var sticky = sectionEl.querySelector('[id^="bundler-sticky-"]');
      if (!bundleRoot || !sticky || bundleRoot.dataset.rathnaMinWired === '1') return;
      bundleRoot.dataset.rathnaMinWired = '1';

      var sectionId = sticky.id.replace('bundler-sticky-', '');
      var xData = bundleRoot.getAttribute('x-data') || '';
      var minMatch = xData.match(/xProductBundle\(\s*'[^']+'\s*,\s*(\d+)/);
      var minimumItems = minMatch ? parseInt(minMatch[1], 10) : 3;

      function distinctCount(productsBundle) {
        if (!productsBundle || !productsBundle.length) return 0;
        return new Set(productsBundle.map(function (item) {
          return String(item.product_id != null ? item.product_id : item.id);
        })).size;
      }

      function applyMinState(productsBundle) {
        var count = distinctCount(productsBundle);
        var met = count >= minimumItems;
        var hasItems = count > 0;
        sticky.classList.toggle('rathna-occasion-min-met', met);
        sticky.classList.toggle('rathna-occasion-has-items', hasItems);
        sticky.setAttribute('data-rathna-min-met', met ? 'true' : 'false');
        sticky.setAttribute('data-rathna-has-items', hasItems ? 'true' : 'false');
        setOccasionMinNote(sticky, count, minimumItems);
      }

      document.addEventListener('eurus:product-bundle:productsList-changed-' + sectionId, function (e) {
        applyMinState(e.detail && e.detail.productsBundle);
      });

      refreshOccasionBundleMinState(sectionEl);
    });
  }

  function runHomeLayoutEnhancements() {
    initFestiveSectionHeadings();
    initOccasionTabs();
    wireOccasionBundleMinimumCta();
    initBuyUnderJump();
    initBrandLogoCards();
    initShopByUseCards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runHomeLayoutEnhancements);
  } else {
    runHomeLayoutEnhancements();
  }

  document.addEventListener('shopify:section:load', runHomeLayoutEnhancements);
  window.addEventListener('load', runHomeLayoutEnhancements);
})();
